import React, { useEffect, useState } from 'react';
import { getHistory, imageUrl } from '../../api/endpoints';
import ModalShell from '../../components/ModalShell';
import { IconBalance } from '../../components/icons/AnalyzerIcons';
import type { HistoryItem, ProductScanHistoryJob } from '../../types/api';
import { getErrorMessage } from '../../utils/errors';
import { areSameProductReferences, createProductReference, type ProductReference } from '../../utils/productIdentity';
import type { SideProduct } from './productSideFlow/reducer';

/** Product scan rows the picker reads (newest first): single, comparison and compatibility. */
const HISTORY_LIMIT = 50;

/** One product of a history row. */
type ScannedProduct = { productId: number; name?: string; brand?: string; imageUrl?: string; ingredientsState?: string };

function sidesOf(item: HistoryItem): ScannedProduct[] {
  const job = item.job as ProductScanHistoryJob;
  const a: ScannedProduct = {
    productId: job.product_id_a, name: job.name_a, brand: job.brand_a, imageUrl: job.image_url_a, ingredientsState: job.ingredients_state_a,
  };
  if (item.job_type === 'product') return [a];
  return [a, {
    productId: job.product_id_b, name: job.name_b, brand: job.brand_b, imageUrl: job.image_url_b, ingredientsState: job.ingredients_state_b,
  }];
}

/** Every product the user scanned, once (newest row wins). */
function uniqueProducts(items: HistoryItem[]): ScannedProduct[] {
  const seen = new Set<number>();
  return items
    .filter((item) => item.job_type === 'product' || item.job_type === 'comparison' || item.job_type === 'compatibility')
    .flatMap(sidesOf)
    .filter((p) => {
      if (!p.productId || seen.has(p.productId)) return false;
      seen.add(p.productId);
      return true;
    });
}

function toSideProduct(p: ScannedProduct): SideProduct {
  return {
    productId: p.productId,
    name: p.name ?? p.brand ?? `Product #${p.productId}`,
    ...(p.brand && p.name ? { brand: p.brand } : {}),
    ...(p.imageUrl ? { imageUrl: p.imageUrl } : {}),
    verified: false,
  };
}

/** Picks a side from the user's own product scans, private label submissions included. */
export function HistoryProductPicker({
  targetLabel,
  excludedProduct,
  onSelect,
  onClose,
}: {
  targetLabel: string;
  excludedProduct?: ProductReference;
  onSelect: (product: SideProduct) => void;
  onClose: () => void;
}) {
  const [items, setItems] = useState<ScannedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getHistory({ type: 'product', limit: HISTORY_LIMIT })
      .then((res) => { if (alive) setItems(uniqueProducts(res.items)); })
      .catch((error_: unknown) => { if (alive) setError(getErrorMessage(error_, 'Failed to load your scans')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <ModalShell title="Choose from your scans" subtitle={targetLabel} onClose={onClose} widthClass="max-w-xl">
      <div className="p-4 sm:p-5 stack-3">
        {loading && <p className="t-small t-mute">Loading your scans…</p>}
        {error && <div className="notice notice--error">{error}</div>}
        {!loading && !error && items.length === 0 && (
          <div className="card card--bordered" style={{ padding: 'var(--s-4)', textAlign: 'center' }}>
            <p className="t-small" style={{ fontWeight: 600 }}>No scanned products yet</p>
            <p className="t-small t-mute">Products you scan show up here.</p>
          </div>
        )}
        {items.map((product) => {
          const selected = areSameProductReferences(createProductReference({ productId: product.productId }), excludedProduct);
          const pending = product.ingredientsState === 'awaiting_team';
          const disabled = selected || pending;
          const title = [product.brand, product.name].filter(Boolean).join(' ') || `Product #${product.productId}`;
          const src = product.imageUrl ? imageUrl(product.imageUrl) : null;
          const label = selected
            ? `${title} already selected`
            : pending ? `${title}: ingredients still being checked` : `Use ${title} for ${targetLabel}`;
          return (
            <button
              key={product.productId}
              type="button"
              className="card card--bordered"
              style={{
                padding: 'var(--s-3)', display: 'flex', alignItems: 'center', gap: 'var(--s-3)',
                textAlign: 'left', opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer',
              }}
              disabled={disabled}
              onClick={() => { onSelect(toSideProduct(product)); }}
              aria-label={label}
            >
              <span
                style={{
                  width: 52, height: 52, flexShrink: 0, borderRadius: 'var(--r-sm)', overflow: 'hidden',
                  background: 'var(--c-cream-weak)', display: 'grid', placeItems: 'center',
                }}
              >
                {src
                  ? <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  : <IconBalance />}
              </span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span className="t-small" style={{ display: 'block', fontWeight: 600, color: 'var(--ink)' }}>
                  {product.name ?? title}
                </span>
                {product.brand && <span className="t-small t-mute" style={{ display: 'block' }}>{product.brand}</span>}
              </span>
              <span className={disabled ? 'pill pill--coral' : 'pill pill--teal'}>
                {selected ? 'Already selected' : pending ? 'Checking ingredients' : 'Choose'}
              </span>
            </button>
          );
        })}
      </div>
    </ModalShell>
  );
}
